import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaCreditCard, FaBitcoin, FaUniversity, FaCcStripe, FaPaypal } from 'react-icons/fa';
import { branding } from '../constants/branding';
import { apiService } from '../services/apiService';
import { formatAmount } from '../utils/currency';
import { featureFlags } from '../config/featureFlags';
import { useAppSelector } from '../store/hooks';
import { rememberPendingTrueLayerPaymentId } from '../utils/truelayerPaymentAuth';

interface TradingAccountDepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  accounts: any[];
  tradingWallet: any;
  userId: number;
}

type DepositMethod = 'account' | 'transak' | 'truelayer' | 'stripe' | 'paypal';

type TrueLayerStep =
  | 'select'            // account + amount selection
  | 'picking_provider'  // TrueLayer returned provider list, user must pick a bank
  | 'authorizing'       // provider selected, user redirected to bank
  | 'polling'           // payment settled, waiting for wallet credit
  | 'credited'          // wallet credit confirmed
  | 'failed';           // payment or credit failed

const MIN_TOPUP_AMOUNT = 10;

const TradingAccountDepositModal: React.FC<TradingAccountDepositModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  accounts,
  tradingWallet,
  userId
}) => {
  const [depositMethod, setDepositMethod] = useState<DepositMethod>('account');

  // ── Fiat (internal bank account) state ──────────────────────────────────
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // ── Transak state ────────────────────────────────────────────────────────
  const [transakWidgetUrl, setTransakWidgetUrl] = useState<string | null>(null);
  const [showTransakWidget, setShowTransakWidget] = useState(false);
  const [transakOrderId, setTransakOrderId] = useState<string | null>(null);
  const [transakStatus, setTransakStatus] = useState<string>('Processing...');
  const [stripeSessionId, setStripeSessionId] = useState<string | null>(null);
  const [stripeStatus, setStripeStatus] = useState<string>('PENDING');
  const [paypalOrderId, setPaypalOrderId] = useState<string | null>(null);
  const [paypalStatus, setPaypalStatus] = useState<string>('PENDING');

  // ── TrueLayer top-up state ────────────────────────────────────────────────
  const [tlAccounts, setTlAccounts] = useState<any[]>([]);
  const [tlAccountsLoading, setTlAccountsLoading] = useState(false);
  const [tlSelectedAccountId, setTlSelectedAccountId] = useState<string>('');
  const [tlBalance, setTlBalance] = useState<any>(null);
  const [tlAmount, setTlAmount] = useState<string>('');
  const [tlStep, setTlStep] = useState<TrueLayerStep>('select');
  const [tlPaymentId, setTlPaymentId] = useState<string>('');
  const [tlResourceToken, setTlResourceToken] = useState<string>('');
  const [tlProviders, setTlProviders] = useState<any[]>([]);
  const [tlProviderLoading, setTlProviderLoading] = useState<string>('');
  const [tlCreditedAmount, setTlCreditedAmount] = useState<string>('');
  const [tlInitialWalletBalance, setTlInitialWalletBalance] = useState<number>(0);
  const tlPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tlWalletPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const profile = useAppSelector((state) => state.profile.profile);
  const userRetailPermissions: string[] = (profile as { permissions?: string[] } | null)?.permissions || [];
  const tlEnabled =
    featureFlags.retail.enableTrueLayerKuCoinTopup &&
    featureFlags.retail.enableTrueLayerRetail &&
    userRetailPermissions.includes('VIEW_RETAIL_DASHBOARD_TRUELAYER');

  // ── Reset on open ──────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setDepositMethod('account');
      setSelectedAccountId('');
      setAmount('');
      setError('');
      setTransakWidgetUrl(null);
      setShowTransakWidget(false);
      setTransakOrderId(null);
      setTransakStatus('Processing...');
      setStripeSessionId(null);
      setStripeStatus('PENDING');
      setPaypalOrderId(null);
      setPaypalStatus('PENDING');
      resetTlState();
    }
    return () => {
      clearTlPollers();
    };
  }, [isOpen]);

  // Load TrueLayer linked accounts when user switches to that tab
  useEffect(() => {
    if (depositMethod === 'truelayer' && tlEnabled && tlAccounts.length === 0 && !tlAccountsLoading) {
      loadTlAccounts();
    }
  }, [depositMethod]);

  // Load balance when a TrueLayer account is selected
  useEffect(() => {
    if (tlSelectedAccountId) {
      loadTlBalance(tlSelectedAccountId);
    } else {
      setTlBalance(null);
    }
  }, [tlSelectedAccountId]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const resetTlState = () => {
    setTlAccounts([]);
    setTlSelectedAccountId('');
    setTlBalance(null);
    setTlAmount('');
    setTlStep('select');
    setTlPaymentId('');
    setTlCreditedAmount('');
    setTlInitialWalletBalance(0);
    clearTlPollers();
  };

  const clearTlPollers = () => {
    if (tlPollIntervalRef.current) clearInterval(tlPollIntervalRef.current);
    if (tlWalletPollIntervalRef.current) clearInterval(tlWalletPollIntervalRef.current);
  };

  const generateFundingReference = () =>
    `KC-TL-${userId}-${Date.now()}`;

  // ── TrueLayer data loading ─────────────────────────────────────────────
  const loadTlAccounts = async () => {
    setTlAccountsLoading(true);
    setError('');
    try {
      const res = await apiService.get('/api/banking/truelayer/accounts');
      if (res.success && res.data?.results) {
        setTlAccounts(res.data.results);
      } else if (res.success && Array.isArray(res.data)) {
        setTlAccounts(res.data);
      } else {
        setError(mapTlError(res));
      }
    } catch (err: any) {
      setError(mapTlCatchError(err));
    } finally {
      setTlAccountsLoading(false);
    }
  };

  const loadTlBalance = async (accountId: string) => {
    setTlBalance(null);
    try {
      const res = await apiService.get(`/api/banking/truelayer/accounts/${accountId}/balance`);
      if (res.success && res.data?.results?.[0]) {
        setTlBalance(res.data.results[0]);
      } else if (res.success && res.data?.available !== undefined) {
        setTlBalance(res.data);
      }
    } catch {
      // balance display is non-critical; silent failure
    }
  };

  // ── TrueLayer payment flow ──────────────────────────────────────────────
  const handleTlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const parsedAmount = parseFloat(tlAmount);
    if (!tlSelectedAccountId) { setError('Please select a linked bank account.'); return; }
    if (!tlAmount || parsedAmount < MIN_TOPUP_AMOUNT) {
      setError(`Minimum top-up amount is ${MIN_TOPUP_AMOUNT}.00.`);
      return;
    }

    // TrueLayer merchant account is EUR — all payments must be in EUR regardless of bank account currency.
    const currency = 'EUR';
    const fundingReference = generateFundingReference();

    setLoading(true);
    try {
      // Step 1 — create the TrueLayer payment
      const createRes = await apiService.post('/api/banking/truelayer/payments', {
        amount: parsedAmount,
        currency,
        reference: fundingReference,
        metadata: { purpose: 'KUCOIN_WALLET_TOPUP' },
        payment_method: {
          type: 'bank_transfer',
          provider_selection: { type: 'user_selected' },
          beneficiary: {
            type: 'merchant_account',
            reference: fundingReference.substring(0, 18)
          }
        }
      });

      if (!createRes.success || !createRes.data?.id) {
        setError(mapTlError(createRes));
        return;
      }

      const paymentId: string = createRes.data.id;
      const resourceToken: string = createRes.data.resource_token || '';
      setTlPaymentId(paymentId);
      setTlCreditedAmount(`${parsedAmount.toFixed(2)} ${currency}`);
      setTlInitialWalletBalance(tradingWallet?.availableBalance || 0);

      // Step 2 — start authorization flow.
      // provider_selection is required because payment was created with type: "user_selected".
      // redirect is required so TrueLayer knows where to send the user after bank auth.
      const authRes = await apiService.post(
        `/api/banking/truelayer/payments/${paymentId}/authorization-flow`,
        {
          resource_token: resourceToken,
          provider_selection: {},
          redirect: { return_uri: `${window.location.origin}/truelayer-connect` }
        }
      );

      if (authRes.success && authRes.data?.authorization_flow) {
        const next = authRes.data.authorization_flow.actions?.next;

        // TrueLayer wants the user to pick a bank provider first
        if (next?.type === 'provider_selection' && next?.providers?.length > 0) {
          setTlProviders(next.providers);
          setTlResourceToken(resourceToken);
          setTlStep('picking_provider');
          return;
        }

        // TrueLayer returned a direct redirect action
        if (next?.type === 'redirect' && next?.uri) {
          setTlStep('authorizing');
          window.open(next.uri, '_blank', 'noopener,noreferrer');
          startPaymentPolling(paymentId);
          return;
        }
      }

      // Legacy response shape fallback
      if (authRes.success && authRes.data?.redirect?.uri) {
        setTlStep('authorizing');
        rememberPendingTrueLayerPaymentId(paymentId);
        window.open(authRes.data.redirect.uri, '_blank', 'noopener,noreferrer');
        startPaymentPolling(paymentId);
        return;
      }

      setError('Unable to start authorization. Please try again.');
    } catch (err: any) {
      setError(mapTlCatchError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSelect = async (providerId: string) => {
    setTlProviderLoading(providerId);
    setError('');
    try {
      const res = await apiService.post(
        `/api/banking/truelayer/payments/${tlPaymentId}/authorization-flow/actions/provider-selection`,
        { resource_token: tlResourceToken, provider_id: providerId }
      );

      if (res.success && res.data?.authorization_flow) {
        const next = res.data.authorization_flow.actions?.next;
        if (next?.type === 'redirect' && next?.uri) {
          setTlStep('authorizing');
          rememberPendingTrueLayerPaymentId(tlPaymentId);
          window.open(next.uri, '_blank', 'noopener,noreferrer');
          startPaymentPolling(tlPaymentId);
          return;
        }
      }

      // Some providers return redirect at top level
      if (res.success && res.data?.redirect?.uri) {
        setTlStep('authorizing');
        rememberPendingTrueLayerPaymentId(tlPaymentId);
        window.open(res.data.redirect.uri, '_blank', 'noopener,noreferrer');
        startPaymentPolling(tlPaymentId);
        return;
      }

      setError('Failed to select provider. Please try again.');
    } catch (err: any) {
      setError(mapTlCatchError(err));
    } finally {
      setTlProviderLoading('');
    }
  };

  const startPaymentPolling = (paymentId: string) => {
    const maxAttempts = 36; // 3 min at 5s intervals
    let attempts = 0;
    tlPollIntervalRef.current = setInterval(async () => {
      attempts++;
      try {
        const statusRes = await apiService.get(`/api/banking/truelayer/payments/${paymentId}`);
        const status: string = statusRes.data?.status || '';
        if (status === 'settled') {
          clearInterval(tlPollIntervalRef.current!);
          setTlStep('polling');
          startWalletCreditPolling();
        } else if (status === 'failed') {
          clearInterval(tlPollIntervalRef.current!);
          setError('Your bank declined the payment. No funds have been moved.');
          setTlStep('failed');
        }
      } catch { /* transient error — keep polling */ }

      if (attempts >= maxAttempts) {
        clearInterval(tlPollIntervalRef.current!);
        setTlStep('failed');
        setError('Payment authorization timed out. If you completed it, your balance will update shortly.');
      }
    }, 5000);
  };

  const startWalletCreditPolling = () => {
    const maxAttempts = 36; // 3 min
    let attempts = 0;
    tlWalletPollIntervalRef.current = setInterval(async () => {
      attempts++;
      try {
        const walletRes = await apiService.get(`/api/retail/trading/wallets/${userId}/balance`);
        const newBalance: number = walletRes?.data?.availableBalance ?? walletRes?.data?.available_balance ?? 0;
        if (newBalance > tlInitialWalletBalance) {
          clearInterval(tlWalletPollIntervalRef.current!);
          setTlStep('credited');
          onSuccess();
        }
      } catch { /* keep polling */ }

      if (attempts >= maxAttempts) {
        clearInterval(tlWalletPollIntervalRef.current!);
        setTlStep('credited'); // show soft success — credit is in progress server-side
      }
    }, 5000);
  };

  // ── Fiat (internal account) flow ──────────────────────────────────────
  const handleAccountDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedAccountId) { setError('Please select an account'); return; }
    if (!amount || parseFloat(amount) <= 0) { setError('Please enter a valid amount'); return; }
    setLoading(true);
    try {
      const response = await apiService.post(`/api/retail/trading/wallets/${userId}/fund`, {
        initialAmount: parseFloat(amount),
        currency: tradingWallet?.currency || 'USD',
        fundingSource: 'FIAT',
        sourceAccountId: parseInt(selectedAccountId),
        notes: `Deposit from account ${selectedAccountId}`
      });
      if (response.success) {
        onSuccess();
        setAmount('');
        setSelectedAccountId('');
        onClose();
      } else {
        setError(response.error || 'Deposit failed');
      }
    } catch (err: any) {
      setError(err.message || 'Deposit failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Transak flow ──────────────────────────────────────────────────────
  const handleTransakDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) { setError('Please enter a valid amount'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await apiService.post('/api/transak/create-order', {
        fiatAmount: parseFloat(amount),
        fiatCurrency: tradingWallet?.currency || 'EUR',
        cryptoCurrency: 'USDT'
      });
      if (response.success && response.data) {
        const { widgetUrl, orderId } = response.data;
        if (widgetUrl && orderId) {
          setTransakWidgetUrl(widgetUrl);
          setTransakOrderId(orderId);
          setShowTransakWidget(true);
          setError('');
          pollTransakStatus(orderId);
        } else {
          setError('Invalid response from Transak service');
        }
      } else {
        setError(response.error || 'Failed to create Transak order');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create Transak order');
    } finally {
      setLoading(false);
    }
  };

  const handleStripeDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) { setError('Please enter a valid amount'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await apiService.post('/api/stripe/create-checkout-session', {
        fiatAmount: parseFloat(amount),
        fiatCurrency: tradingWallet?.currency || 'EUR'
      });
      if (response.success && response.data?.checkoutUrl && response.data?.sessionId) {
        const sessionId = response.data.sessionId as string;
        setStripeSessionId(sessionId);
        setStripeStatus('PROCESSING');
        window.open(response.data.checkoutUrl, '_blank', 'noopener,noreferrer');
        pollStripeStatus(sessionId);
      } else {
        setError(response.error || 'Failed to create Stripe checkout session');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create Stripe checkout session');
    } finally {
      setLoading(false);
    }
  };

  const pollStripeStatus = async (sessionId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const response = await apiService.get(`/api/stripe/transaction/${sessionId}`);
        if (response.success && response.data) {
          const status = response.data.status || 'PROCESSING';
          setStripeStatus(status);
          if (status === 'COMPLETED') {
            clearInterval(interval);
            onSuccess();
            setTimeout(onClose, 1500);
          } else if (status === 'FAILED' || status === 'CANCELLED') {
            clearInterval(interval);
            setError(response.data.errorMessage || 'Payment was cancelled or failed');
          }
        }
      } catch {
        // keep polling for transient failures
      }
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setError('Stripe payment timeout. Please check your transaction status.');
      }
    }, 5000);
  };

  const handlePayPalDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) { setError('Please enter a valid amount'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await apiService.post('/api/paypal/create-order', {
        fiatAmount: parseFloat(amount),
        fiatCurrency: tradingWallet?.currency || 'EUR'
      });
      if (response.success && response.data?.approvalUrl && response.data?.orderId) {
        const orderId = response.data.orderId as string;
        setPaypalOrderId(orderId);
        setPaypalStatus('PROCESSING');
        window.open(response.data.approvalUrl, '_blank', 'noopener,noreferrer');
        pollPayPalStatus(orderId);
      } else {
        setError(response.error || 'Failed to create PayPal order');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create PayPal order');
    } finally {
      setLoading(false);
    }
  };

  const pollPayPalStatus = async (orderId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const response = await apiService.get(`/api/paypal/transaction/${orderId}`);
        if (response.success && response.data) {
          const status = response.data.status || 'PROCESSING';
          setPaypalStatus(status);
          if (status === 'COMPLETED') {
            clearInterval(interval);
            onSuccess();
            setTimeout(onClose, 1500);
          } else if (status === 'FAILED' || status === 'CANCELLED') {
            clearInterval(interval);
            setError(response.data.errorMessage || 'Payment was cancelled or failed');
          }
        }
      } catch {
        // keep polling for transient failures
      }
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setError('PayPal payment timeout. Please check your transaction status.');
      }
    }, 5000);
  };

  const pollTransakStatus = async (orderId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const response = await apiService.get(`/api/transak/transaction/${orderId}`);
        if (response.success && response.data) {
          const status = response.data.status;
          setTransakStatus(status);
          if (status === 'COMPLETED') {
            clearInterval(interval);
            setShowTransakWidget(false);
            onSuccess();
            setTimeout(onClose, 2000);
          } else if (status === 'FAILED' || status === 'CANCELLED') {
            clearInterval(interval);
            setShowTransakWidget(false);
            setError('Payment was cancelled or failed');
          }
        }
      } catch { /* keep polling */ }
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setError('Payment timeout. Please check your transaction status.');
      }
    }, 5000);
  };

  // ── Error mappers ────────────────────────────────────────────────────
  const mapTlError = (res: any): string => {
    const msg: string = res?.message || res?.error || '';
    if (msg.toLowerCase().includes('reconnect') || msg.toLowerCase().includes('token')) {
      return 'Your bank connection has expired. Please reconnect from the dashboard.';
    }
    if (msg.toLowerCase().includes('merchant account currency') || msg.toLowerCase().includes('currency is not')) {
      return 'Your bank account currency does not match the payment gateway currency. Please contact support or try a different account.';
    }
    if (msg.toLowerCase().includes('currency')) {
      return 'The selected account currency is not supported. Please use a GBP or EUR account.';
    }
    return msg || 'Failed to process bank payment. Please try again.';
  };

  const mapTlCatchError = (err: any): string => {
    const status = err?.response?.status;
    if (status === 401) return 'Session expired. Please log in again.';
    if (status === 403) return 'Access denied. Please reconnect your bank account.';
    return err?.message || 'Network error. Please check your connection.';
  };

  if (!isOpen) return null;

  const selectedAccount = accounts.find(acc => acc.id.toString() === selectedAccountId);
  // TrueLayer merchant account is EUR — display and send EUR for all bank transfer top-ups.
  const tlCurrency = 'EUR';

  return (
    <div style={modalOverlayStyle}>
      <div style={getModalContentStyle(showTransakWidget)}>
        {/* Header */}
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: branding.textColor }}>
            Deposit to Trading Account
          </h3>
          <button onClick={onClose} style={closeBtnStyle}><FaTimes /></button>
        </div>

        {/* Current balance */}
        {tradingWallet && (
          <div style={balanceBannerStyle}>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Current Trading Balance</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#086afb' }}>
              {formatAmount(tradingWallet.totalBalance || tradingWallet.availableBalance || 0, tradingWallet.currency || 'USD')}
            </div>
          </div>
        )}

        {/* Method tabs */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 12, fontWeight: 600, color: branding.textColor }}>
            Deposit Method
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <MethodTab
              active={depositMethod === 'account'}
              onClick={() => setDepositMethod('account')}
              icon={<FaCreditCard />}
              label="From Bank Account"
            />
            <MethodTab
              active={depositMethod === 'stripe'}
              onClick={() => setDepositMethod('stripe')}
              icon={<FaCcStripe />}
              label="Via Stripe (Card)"
            />
            <MethodTab
              active={depositMethod === 'paypal'}
              onClick={() => setDepositMethod('paypal')}
              icon={<FaPaypal />}
              label="Via PayPal"
            />
            <MethodTab
              active={depositMethod === 'transak'}
              onClick={() => setDepositMethod('transak')}
              icon={<FaBitcoin />}
              label="Via Transak (Crypto)"
            />
            {tlEnabled && (
              <MethodTab
                active={depositMethod === 'truelayer'}
                onClick={() => { setError(''); setDepositMethod('truelayer'); }}
                icon={<FaUniversity />}
                label="Via Bank (TrueLayer)"
              />
            )}
          </div>
        </div>

        {/* ── Fiat panel ───────────────────────────────────────────── */}
        {depositMethod === 'account' && (
          <form onSubmit={handleAccountDeposit}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>From Account</label>
              {accounts.length === 0 ? (
                <div style={emptyStateStyle}>
                  No checking or savings accounts available. Please create an account first.
                </div>
              ) : (
                <select value={selectedAccountId} onChange={e => setSelectedAccountId(e.target.value)}
                  style={selectStyle} required>
                  <option value="">Select an account</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.type
                        ? account.type.charAt(0) + account.type.slice(1).toLowerCase()
                        : 'Account'} — {formatAmount(account.balance || 0, account.currency || 'USD')}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {selectedAccount && (
              <div style={infoBoxStyle}>
                Available Balance: <strong>{formatAmount(selectedAccount.balance, selectedAccount.currency)}</strong>
              </div>
            )}

            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Amount</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="0.00" step="0.01" min="0.01" style={inputStyle} required />
            </div>

            {error && <ErrorAlert message={error} />}

            <ActionButtons
              onCancel={onClose}
              submitLabel={loading ? 'Processing...' : 'Deposit'}
              disabled={loading}
            />
          </form>
        )}

        {/* ── Transak panel ────────────────────────────────────────── */}
        {depositMethod === 'transak' && showTransakWidget && transakWidgetUrl ? (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: branding.textColor }}>
                Complete Payment via Transak
              </h4>
              <button type="button" onClick={() => { setShowTransakWidget(false); setTransakWidgetUrl(null); setTransakOrderId(null); }}
                style={backBtnStyle}>
                Back
              </button>
            </div>
            {transakOrderId && (
              <div style={infoBoxStyle}>
                Order ID: <strong>{transakOrderId}</strong> | Status: <strong>{transakStatus}</strong>
              </div>
            )}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', height: 600, width: '100%' }}>
              <iframe src={transakWidgetUrl} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                title="Transak Payment Widget" allow="camera; microphone; payment" />
            </div>
            <div style={{ ...infoBoxStyle, textAlign: 'center', marginTop: 12 }}>
              After completing payment, your account will be credited automatically.
            </div>
          </div>
        ) : depositMethod === 'transak' ? (
          <div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Amount ({tradingWallet?.currency || 'EUR'})</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="0.00" step="0.01" min="10.00" style={inputStyle} />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                Minimum: {formatAmount(10, tradingWallet?.currency || 'EUR')}
              </div>
            </div>
            {error && <ErrorAlert message={error} />}
            <div style={{ ...infoBoxStyle, background: '#f0f8ff' }}>
              <strong>Note:</strong> The Transak payment widget will open below. After payment is confirmed,
              your trading account will be credited automatically.
            </div>
            <ActionButtons
              onCancel={onClose}
              submitLabel={loading ? 'Loading...' : 'Continue to Transak'}
              disabled={loading || !amount}
              onSubmit={handleTransakDeposit}
              type="button"
            />
          </div>
        ) : null}

        {/* ── Stripe panel ─────────────────────────────────────────── */}
        {depositMethod === 'stripe' && (
          <div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Amount ({tradingWallet?.currency || 'EUR'})</label>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="10.00"
                style={inputStyle}
              />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                Minimum: {formatAmount(10, tradingWallet?.currency || 'EUR')}
              </div>
            </div>

            {stripeSessionId && (
              <div style={infoBoxStyle}>
                Stripe Session: <strong>{stripeSessionId}</strong> | Status: <strong>{stripeStatus}</strong>
              </div>
            )}

            {error && <ErrorAlert message={error} />}

            <div style={{ ...infoBoxStyle, background: '#f0f8ff' }}>
              <strong>Note:</strong> A secure Stripe checkout page opens in a new tab. Once payment is confirmed,
              your account will be credited automatically.
            </div>

            <ActionButtons
              onCancel={onClose}
              submitLabel={loading ? 'Loading...' : 'Continue to Stripe'}
              disabled={loading || !amount}
              onSubmit={handleStripeDeposit}
              type="button"
            />
          </div>
        )}

        {/* ── PayPal panel ─────────────────────────────────────────── */}
        {depositMethod === 'paypal' && (
          <div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Amount ({tradingWallet?.currency || 'EUR'})</label>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="10.00"
                style={inputStyle}
              />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                Minimum: {formatAmount(10, tradingWallet?.currency || 'EUR')}
              </div>
            </div>

            {paypalOrderId && (
              <div style={infoBoxStyle}>
                PayPal Order: <strong>{paypalOrderId}</strong> | Status: <strong>{paypalStatus}</strong>
              </div>
            )}

            {error && <ErrorAlert message={error} />}

            <div style={{ ...infoBoxStyle, background: '#f0f8ff' }}>
              <strong>Note:</strong> A secure PayPal page opens in a new tab. Once payment is confirmed,
              your account will be credited automatically.
            </div>

            <ActionButtons
              onCancel={onClose}
              submitLabel={loading ? 'Loading...' : 'Continue to PayPal'}
              disabled={loading || !amount}
              onSubmit={handlePayPalDeposit}
              type="button"
            />
          </div>
        )}

        {/* ── TrueLayer top-up panel ────────────────────────────────── */}
        {depositMethod === 'truelayer' && (
          <div>
            {tlStep === 'select' && (
              <form onSubmit={handleTlSubmit}>
                {tlAccountsLoading ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#666' }}>Loading linked accounts…</div>
                ) : tlAccounts.length === 0 ? (
                  <div style={emptyStateStyle}>
                    No linked bank accounts found.{' '}
                    <a href="#/dashboard" style={{ color: '#086afb' }}>
                      Connect your bank account
                    </a>{' '}
                    first, then return here.
                  </div>
                ) : (
                  <>
                    <div style={fieldGroupStyle}>
                      <label style={labelStyle}>Linked Bank Account</label>
                      <select
                        value={tlSelectedAccountId}
                        onChange={e => setTlSelectedAccountId(e.target.value)}
                        style={selectStyle}
                        required
                      >
                        <option value="">Select a linked account</option>
                        {tlAccounts.map(a => (
                          <option key={a.account_id || a.id} value={a.account_id || a.id}>
                            {a.provider?.display_name || a.provider_name || 'Bank'} —{' '}
                            {a.account_number?.iban || a.display_name || a.account_id || a.id} ({a.currency || '?'})
                          </option>
                        ))}
                      </select>
                    </div>

                    {tlBalance && (
                      <div style={infoBoxStyle}>
                        Available Balance:{' '}
                        <strong>
                          {formatAmount(tlBalance.available ?? tlBalance.current ?? 0, tlCurrency)}
                        </strong>
                      </div>
                    )}

                    <div style={{ fontSize: 12, color: '#555', background: '#f0f4ff', border: '1px solid #c7d7fc', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                      Payments are processed in <strong>EUR</strong>. Your bank will handle any currency conversion if your account is in a different currency.
                    </div>

                    <div style={fieldGroupStyle}>
                      <label style={labelStyle}>Amount ({tlCurrency})</label>
                      <input
                        type="number"
                        value={tlAmount}
                        onChange={e => setTlAmount(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min={MIN_TOPUP_AMOUNT}
                        style={inputStyle}
                        required
                      />
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        Minimum: {formatAmount(MIN_TOPUP_AMOUNT, tlCurrency)}
                      </div>
                    </div>

                    <div style={{ ...infoBoxStyle, background: '#f0f8ff' }}>
                      <strong>How it works:</strong> Your bank will ask you to authorise a payment to this
                      platform. Once settled, your KuCoin trading wallet is credited automatically (usually
                      within a few minutes).
                    </div>

                    {error && <ErrorAlert message={error} />}

                    <ActionButtons
                      onCancel={onClose}
                      submitLabel={loading ? 'Creating payment…' : 'Continue to Bank'}
                      disabled={loading || !tlSelectedAccountId || !tlAmount}
                    />
                  </>
                )}
              </form>
            )}

            {tlStep === 'picking_provider' && (
              <div>
                <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 12, color: '#1a1a2e' }}>
                  Select your bank to authorise the payment
                </p>
                {error && <ErrorAlert message={error} />}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, maxHeight: 380, overflowY: 'auto' }}>
                  {tlProviders.map((p: any) => (
                    <button
                      key={p.id}
                      onClick={() => handleProviderSelect(p.id)}
                      disabled={!!tlProviderLoading}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px', border: '1px solid #e0e0e0',
                        borderRadius: 8, background: tlProviderLoading === p.id ? '#f0f4ff' : '#fff',
                        cursor: tlProviderLoading ? 'wait' : 'pointer',
                        textAlign: 'left', fontSize: 13, fontWeight: 500,
                        opacity: tlProviderLoading && tlProviderLoading !== p.id ? 0.5 : 1,
                        transition: 'all 0.15s'
                      }}
                    >
                      {p.icon_uri && (
                        <img src={p.icon_uri} alt="" style={{ width: 28, height: 28, borderRadius: 4, flexShrink: 0 }} />
                      )}
                      <span>
                        {tlProviderLoading === p.id ? 'Connecting…' : p.display_name}
                        {p.country_code && (
                          <span style={{ display: 'block', fontSize: 11, color: '#888', fontWeight: 400 }}>
                            {p.country_code}
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => { setTlStep('select'); setTlProviders([]); setError(''); }}
                  style={{ marginTop: 14, fontSize: 13, color: '#666', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  ← Back
                </button>
              </div>
            )}

            {tlStep === 'authorizing' && (
              <StatusCard
                icon="🏦"
                title="Waiting for payment authorization"
                body="A new tab was opened for you to authorize the payment with your bank. Come back here once you've completed it."
                subBody={`Payment ID: ${tlPaymentId}`}
                color="#086afb"
              />
            )}

            {tlStep === 'polling' && (
              <StatusCard
                icon="⏳"
                title="Payment received — crediting wallet…"
                body="Your payment has settled. We're now crediting your KuCoin trading wallet. This usually takes less than a minute."
                color="#fbb445"
              />
            )}

            {tlStep === 'credited' && (
              <div>
                <StatusCard
                  icon="✅"
                  title="Wallet credited!"
                  body={`${tlCreditedAmount} has been credited to your KuCoin trading wallet. Your available balance has been updated.`}
                  color="#0dca9d"
                />
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <button onClick={onClose} style={{ ...primaryBtnStyle, width: 'auto', padding: '10px 32px' }}>
                    Close
                  </button>
                </div>
              </div>
            )}

            {tlStep === 'failed' && (
              <div>
                {error && <ErrorAlert message={error} />}
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <button type="button" onClick={() => { setTlStep('select'); setError(''); }} style={secondaryBtnStyle}>
                    Try Again
                  </button>
                  <button type="button" onClick={onClose} style={primaryBtnStyle}>Close</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const MethodTab: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }> =
  ({ active, onClick, icon, label }) => (
    <button type="button" onClick={onClick} style={{
      flex: 1,
      minWidth: 130,
      padding: 14,
      border: `2px solid ${active ? branding.primaryColor : '#e0e0e0'}`,
      borderRadius: 8,
      background: active ? `${branding.primaryColor}10` : 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      fontSize: 13,
      color: active ? branding.primaryColor : '#666',
      fontWeight: active ? 600 : 400,
      transition: 'all 0.15s ease'
    }}>
      <span style={{ color: active ? branding.primaryColor : '#888' }}>{icon}</span>
      {label}
    </button>
  );

const ErrorAlert: React.FC<{ message: string }> = ({ message }) => (
  <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
    {message}
  </div>
);

const StatusCard: React.FC<{ icon: string; title: string; body: string; subBody?: string; color: string }> =
  ({ icon, title, body, subBody, color }) => (
    <div style={{
      border: `2px solid ${color}`,
      borderRadius: 12,
      padding: 24,
      textAlign: 'center',
      background: `${color}10`
    }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 14, color: '#555', marginBottom: subBody ? 8 : 0 }}>{body}</div>
      {subBody && <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{subBody}</div>}
    </div>
  );

interface ActionButtonsProps {
  onCancel?: () => void;
  submitLabel: string;
  disabled?: boolean;
  onSubmit?: () => void;
  type?: 'submit' | 'button';
}
const ActionButtons: React.FC<ActionButtonsProps> = ({ onCancel, submitLabel, disabled, onSubmit, type = 'submit' }) => (
  <div style={{ display: 'flex', gap: 12 }}>
    {onCancel && (
      <button type="button" onClick={onCancel} style={secondaryBtnStyle}>Cancel</button>
    )}
    <button type={type} onClick={onSubmit} disabled={disabled} style={{ ...primaryBtnStyle, background: disabled ? '#9ca3af' : branding.primaryColor, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {submitLabel}
    </button>
  </div>
);

// ── Styles ────────────────────────────────────────────────────────────────────
const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', zIndex: 10000, padding: 20
};
const getModalContentStyle = (showWidget: boolean): React.CSSProperties => ({
  background: 'white', borderRadius: 16, padding: 32,
  maxWidth: showWidget ? 900 : 520, width: '100%',
  maxHeight: '90vh', overflowY: 'auto', transition: 'max-width 0.3s ease'
});
const modalHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 24, paddingBottom: 16, borderBottom: `2px solid ${branding.primaryColor}`
};
const balanceBannerStyle: React.CSSProperties = {
  background: '#e3f2fd', padding: 16, borderRadius: 8, marginBottom: 20, border: '2px solid #086afb'
};
const closeBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', fontSize: 20, color: '#666', cursor: 'pointer', padding: 8, borderRadius: 4
};
const fieldGroupStyle: React.CSSProperties = { marginBottom: 18 };
const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 8, fontWeight: 600, color: branding.textColor, fontSize: 14 };
const inputStyle: React.CSSProperties = { width: '100%', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const selectStyle: React.CSSProperties = { ...inputStyle };
const infoBoxStyle: React.CSSProperties = { background: '#f9fafb', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#555' };
const emptyStateStyle: React.CSSProperties = { padding: 14, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb', color: '#6b7280', fontSize: 14, textAlign: 'center' };
const primaryBtnStyle: React.CSSProperties = { flex: 1, padding: 12, border: 'none', borderRadius: 8, background: branding.primaryColor, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 };
const secondaryBtnStyle: React.CSSProperties = { flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', color: branding.textColor, cursor: 'pointer', fontSize: 14, fontWeight: 600 };
const backBtnStyle: React.CSSProperties = { padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', color: branding.textColor, cursor: 'pointer', fontSize: 12, fontWeight: 600 };

export default TradingAccountDepositModal;
